import * as React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideIcon,
} from 'lucide-react';

import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

type StatusPillVariant = 'success' | 'destructive' | 'warning' | 'info' | 'muted';

const VARIANT_DEFAULT_ICON: Record<StatusPillVariant, LucideIcon> = {
  success: CheckCircle2,
  destructive: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  muted: Info,
};

const VARIANT_CLASSES: Record<StatusPillVariant, string> = {
  success: 'bg-success-soft text-success',
  destructive: 'bg-destructive-soft text-destructive',
  warning: 'bg-warning-soft text-warning-soft-foreground',
  info: 'bg-info-soft text-info-soft-foreground',
  muted: 'bg-muted text-muted-foreground',
};

interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: StatusPillVariant;
  /** Override the default Lucide icon for this variant. Pass `null` to hide. */
  icon?: LucideIcon | null;
  children: React.ReactNode;
}

/**
 * Semantic status chip with success/destructive/warning/info/muted variants — the standard way to render record status.
 *
 * @category Data
 */
export const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ variant, icon, className, children, ...props }, ref) => {
    const Icon = icon === null ? null : icon ?? VARIANT_DEFAULT_ICON[variant];
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5',
          VARIANT_CLASSES[variant],
          className,
        )}
        {...props}
      >
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
        <Typography variant="small" className="font-medium">
          {children}
        </Typography>
      </span>
    );
  },
);
StatusPill.displayName = 'StatusPill';

export type { StatusPillVariant };
